import { useLocation } from 'react-router-dom';
import { Header } from 'layout';
import { IraUserCheckPayload } from 'services/requests';
import { useNavigate } from 'hooks/routes';
import { useIraUserForm } from 'hooks/forms';
import { useAddIraUser } from 'hooks/api/iraUser';
import { useGetProfileList } from 'hooks/api/profiles';
import { useUnsaveChangesModal } from 'hooks/modals';
import { useGetProfile } from 'hooks/api/login';
import { useGetDefaultUserManagers } from 'hooks/api/userManager';
import { useGetEmployer } from 'hooks/api/employer';
import {
  formatInputDateForBE,
  parseInputDate,
  getProfileStatus,
  mapCustodianTypeValueToKey,
} from 'utils';
import { USER_STATUS } from 'constants/status';
import { mapIndependentIraTypeValueToKey } from 'screens/IndependentIra/IraIndependentIra.utils';
import Button from 'components/Button';
import { EntityStatus } from 'components/Label';
import SaveDiscardingChangesModal from 'components/UnsaveChangesModal';
import IraUserForm from '../components/IraUserForm';
import messages from './IraUserNew.messages';
import styles from './IraUserNew.module.css';

const protectFieldsInState = (state: unknown): state is IraUserCheckPayload =>
  !!(state && (state as IraUserCheckPayload));

export default function IraUserNew(): JSX.Element {
  const { toBack } = useNavigate();

  const { mutateAsync: createIraUser, isLoading } = useAddIraUser();

  const { state } = useLocation();
  const profileLocationState = protectFieldsInState(state) ? state : null;
  const { data: otherAccounts } = useGetProfileList(profileLocationState?.email);

  const { data: profileData } = useGetProfile();
  const { defaultEtzFee, defaultCustodyFeePercentageIraClub } = profileData || {};

  const { data: defaultUserManagersData } = useGetDefaultUserManagers();

  const { data: employerData } = useGetEmployer(profileLocationState?.employerId);

  const form = useIraUserForm(profileLocationState);
  const { handleSubmit, getValues } = form;

  const onAddIraUserClick = async (): Promise<void> => {
    const payload = getValues();
    await createIraUser({
      ...payload,
      custodianType: mapCustodianTypeValueToKey(payload.custodianType) ?? '',
      profileType: mapIndependentIraTypeValueToKey(payload.profileType) ?? '',
      custodyFeeAbsolute: payload.custodyFeeAbsolute ?? '',
      custodyFeePercentage: payload.custodyFeePercentage ?? '',
      nextBillingDate: payload.nextBillingDate
        ? formatInputDateForBE(parseInputDate(payload.nextBillingDate))
        : '',
    });
  };

  const unsaveChangesModal = useUnsaveChangesModal(form, form.reset);

  return (
    <>
      <Header
        title={messages.title}
        complexComponent={true}
        label={{
          status: getProfileStatus(profileLocationState?.profileStatus),
          entityStatus: EntityStatus.User,
        }}
        showArrow={true}
        contentClassName={styles.newIraUser__buttons}
      >
        <Button variant={'outline'} onClick={toBack}>
          {messages.buttonCancel}
        </Button>
        <Button onClick={handleSubmit(onAddIraUserClick)} isLoading={isLoading}>
          {messages.buttonAdd}
        </Button>
      </Header>

      <SaveDiscardingChangesModal {...unsaveChangesModal} />

      <IraUserForm
        form={form}
        profileStatus={USER_STATUS.draft}
        isNewUser={true}
        hasOtherAccount={!!profileLocationState?.profileStatus}
        otherAccounts={otherAccounts}
        email={profileLocationState?.email}
        defaultEtzFee={defaultEtzFee}
        defaultCustodyFeePercentageIraClub={defaultCustodyFeePercentageIraClub}
        custodianType={profileLocationState?.custodianType}
        profileType={profileLocationState?.profileType}
        defaultUserManager={defaultUserManagersData}
        employerName={employerData?.name}
        // User can edit the custodian when there is no default value
        shouldEditCustodian={!profileLocationState?.custodianType}
      />
    </>
  );
}
